<template>
  <div>
    <div class="btn-group">
      <button 
        v-for="(priceGroup, index) in servicePricingData.servicePriceGroupList"
        :key="index" class="btn btn-sm btn-white"
        :class="[$data.selectedPriceGroup == index ? 'active' : '']"
        @click.prevent="$data.selectedPriceGroup = index">
        {{priceGroup.name}}
      </button>
    </div>
    <StaticDataTable class="static-table-list mt-4" v-if="servicePricingData && servicePricingData.servicePriceGroupList && servicePricingData.servicePriceGroupList[selectedPriceGroup]">
      <template v-slot:header>
        <tr>
          <th>Code</th>
          <th width="20%">{{editing ? 'Alias' : 'Name'}}</th>
          <template v-for="priceRange in servicePricingData.servicePriceGroupList[selectedPriceGroup].servicePriceRanges">
            <th :key="priceRange.id">{{priceRange.name}}</th>
          </template>
          <th>Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="(stock, stockIndex) in servicePricingData.stockServicePriceList" :key="stock.id">
          <td class="font-weight-bold">
            <router-link :to="`/inventory/stock/${stock.id}`">{{stock.code}}</router-link>
            <span v-if="stock.isPendingUpdate" class="pricing-pending-update">
              &nbsp;<Tooltip label="Price updates pending"><i class="far fa-clock"></i></Tooltip>
            </span>
          </td>
          <td>
            <span v-if="!editing" :class="stock.alias ? 'font-italic' : ''">{{stock.alias ? stock.alias : stock.name}}</span>
            <FormInputText 
              v-if="editing"
              :value="stock.alias"
              :ref="`stock-alias-${stock.id}-${selectedPriceGroup}`"
              @input="setStockAlias(stock, $event)"
              placeholder="Stock Alias"
            />
          </td>
          <template v-for="(priceRange, index) in servicePricingData.servicePriceGroupList[selectedPriceGroup].servicePriceRanges">
            <td :key="priceRange.id + '-' + stock.id">
              <template v-if="!editing || stock.isPendingUpdate">{{getRangePrice(stock, priceRange.id)}}</template>
              <span v-show="editing && stock.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              <FormInputCurrency 
                v-if="editing && !stock.isPendingUpdate"
                :value="getRangePriceValue(stock, priceRange.id)"
                @input="setRangePriceValue(stock, priceRange.id, $event)"
                @tab="onPriceTabbed($event, stockIndex, index)"
              />
            </td>
          </template>
          <td>
            <a @click.prevent="removeEditingGlassPricing(stock.id)" v-if="editing && !stock.isPendingUpdate"><i class="far fa-trash-alt"></i></a>
            <ActionDropMenu :tableAction="true" :forceDropdown="true" v-if="!editing && stock.isPendingUpdate && userHasReadPermission(PermissionTypes.PricingPermissionTypes.pricingApproval)">
              <DropdownMenuItem :link="`/pricing/requests/${stock.stockPriceRequestGroupId}`" icon="fa-clock" label="Show Pending Prices">Show Pending Prices</DropdownMenuItem>
            </ActionDropMenu>
          </td>
        </tr>

        <tr v-if="editing && servicePricingData.servicePriceGroupList && addingNewStock">
          <td :colspan="2">
            <div class="alert alert-warning" v-if="editingError">
              <i class="far fa-exclamation-triangle"></i>&nbsp;&nbsp;<strong>Error:</strong> {{editingError}}
            </div>
            <FormSearchableSelect :ref="`newStockInput-${selectedPriceGroup}`" :options="lookupStockList" v-model="newStockId" @input="newStockSelected" placeholder="Stock Code" :loading="loading" v-if="addingNewStock" />
          </td>
          <td :colspan="servicePricingData.servicePriceGroupList[selectedPriceGroup].servicePriceRanges.length + 1">
            <ActionButton type="cancel" class="btn-xs ml-2" @click="cancelAddingStockItem()"> Cancel</ActionButton>
          </td>
        </tr>
        <tr v-if="editing && servicePricingData.servicePriceGroupList && !addingNewStock">
          <td :colspan="servicePricingData.servicePriceGroupList[selectedPriceGroup].servicePriceRanges.length + 3">
            <ActionButton type="edit" class="btn-xs" @click="newStockButtonPressed" v-if="!addingNewStock"> New Stock</ActionButton>
          </td>
        </tr>

      </template>
    </StaticDataTable>
    <!--
    <div v-if="editing && servicePricingData.servicePriceGroupList">
      <div class="alert alert-warning" v-if="editingError">
        <i class="far fa-exclamation-triangle"></i>&nbsp;&nbsp;<strong>Error:</strong> {{editingError}}
      </div>
      <div class="row">
        <div class="col-lg-10">
          <ActionButton type="edit" class="btn-xs" @click="addingNewStock=true" v-if="!addingNewStock"> New Stock</ActionButton>
          <FormSearchableSelect :options="lookupStockList" v-model="newStockId" placeholder="Stock Code" :loading="loading" v-if="addingNewStock" />
        </div>
        <div class="col-lg-2" v-if="addingNewStock">
          <ActionButton type="cancel" class="btn-xs ml-2" @click="cancelAddingStockItem()"> Cancel</ActionButton>
          <ActionButton type="new" class="btn-xs ml-2" @click="addNewStockItem(newStockId)"> Add</ActionButton>
        </div>
      </div>
    </div>
    -->
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import StaticDataTable from '@/components/table/staticDataTable'
import { InventoryStockTypes, StockPriceTypes } from '@/store/types'

export default {
  name: 'SupplierServicePriceList',
  components: {
    StaticDataTable
  },
  props: {
    contactId: Number,
    supplierCurrencyCode: String,
    supplierServicePricingData: Object,
    editing: Boolean,
  },
  data() {
    return {
      selectedPriceGroup: 0,
      loading: false,
      stockList: [],
      lookupStockList: [],
      addingNewStock: false,
      newStockId: null,
      servicePricingData: _.cloneDeep(this.$props.supplierServicePricingData),
      editingError: null
    }
  },
  watch: {
    supplierServicePricingData: function() {
      this.$data.servicePricingData = _.cloneDeep(this.$props.supplierServicePricingData)
    }
  },
  computed: {
    ...mapState({
      accountCurrencyCode: state => state.account.currencyCode,
    }),
    selectedPriceGroupId() {
      if(this.$data.servicePricingData == null || this.$data.servicePricingData.servicePriceGroupList == null)
        return 0
      return this.$data.servicePricingData.servicePriceGroupList[this.$data.selectedPriceGroup].id
    },
  },
  mounted() {
    this.loadStockList()
  },
  methods: {
    getRangePriceValue(stock, priceRangeId) {
      var priceInfo = stock.prices.filter(price => price.servicePriceRangeId == priceRangeId && price.stockPriceGroupId == this.selectedPriceGroupId)[0]
      if(priceInfo == null)
        return undefined
      return priceInfo.price
    },
    setRangePriceValue(stock, priceRangeId, value) {
      var priceInfo = stock.prices.filter(price => price.servicePriceRangeId == priceRangeId && price.stockPriceGroupId == this.selectedPriceGroupId)[0]
      if(priceInfo == null) {
        if(value != '' && value != null) {
          stock.prices.push({
            price: value,
           servicePriceRangeId: priceRangeId,
           stockPriceGroupId: this.selectedPriceGroupId,
            landedCost: 0
          })
        }
      } else {
        priceInfo.price = value
      }
      this.$emit('update', this.$data.servicePricingData)
    },
    getRangePrice(stock, priceRangeId) {
      var priceInfo = stock.prices.filter(price => {
        return price.servicePriceRangeId == priceRangeId && 
        price.stockPriceGroupId == this.selectedPriceGroupId
      })[0]
      if(priceInfo == null)
        return '-'
      return this.$options.filters.formatCurrency(priceInfo.price) + ' ' + (this.supplierCurrencyCode || '')
    },
    setStockAlias(stock, alias) {
      stock.alias = alias
      this.$emit('update', this.$data.servicePricingData)
    },
    loadStockList() {
      this.loading = true
      this.$apiService.inventoryService.getStockList().then(response => {
        this.$data.stockList = response.data

        this.$data.lookupStockList = this.$data.stockList
          .filter(stock => stock.isActive && stock.type == InventoryStockTypes.glassService)
          .map(stock => {
            return {
              value: stock.id,
              name: `${stock.code} | ${stock.name}`
            }
          })
      })
      .finally(() => {
        this.loading = false
      })
    },
    newStockButtonPressed() {
      this.addingNewStock = true
      this.editingError = ''
      this.$nextTick(() => {
        this.$refs[`newStockInput-${this.selectedPriceGroup}`].focus()
        this.$refs[`newStockInput-${this.selectedPriceGroup}`].select()
      })
    },
    cancelAddingStockItem() {
      this.addingNewStock = false
      this.newStockId = null
    },
    newStockSelected(stockId) {
      if(this.addNewStockItem(stockId)) {
        this.$nextTick(() => {
          let aliasRef = `stock-alias-${stockId}-${this.selectedPriceGroup}`
          this.$refs[aliasRef][0].focus()
          this.$refs[aliasRef][0].select()
        })
      }
    },
    addNewStockItem(stockId) {
      this.editingError = null
      if(stockId == null)
        return false

      // Check if the code is already in the list
      if(this.servicePricingData.stockServicePriceList.find(stock => stock.id == stockId) != null) {
        this.editingError = 'Stock item is already in the list'
        return false
      }

      // Get stock info from list
      var stockItem = this.$data.stockList.find(stock => stock.id == stockId)
      if(stockItem == null) {
        this.editingError = 'Could not find stock item'
        return false
      }

      // Add an item to the list
      this.servicePricingData.stockServicePriceList.push({
        id: stockId,
        code: stockItem.code,
        name: stockItem.name,
        prices: []
      })

      this.$emit('update', this.$data.servicePricingData)

      this.addingNewStock = false
      this.newStockId = null
      return true
    },
    removeEditingGlassPricing(stockId) {
      // Remove the prices for this service pricing type
      var stock = this.servicePricingData.stockServicePriceList.find(stock => stock.id == stockId)
      if(stock == null)
        return

      // Remove the stock if it has no prices left
      stock.prices = stock.prices.filter(price => price.stockPriceGroupId != this.selectedPriceGroupId)
      if(stock.prices.length == 0) {
        this.servicePricingData.stockServicePriceList = this.servicePricingData.stockServicePriceList.filter(stock => stock.id != stockId)
      }
      
      this.$emit('update', this.$data.servicePricingData)
    },
    onPriceTabbed(evt, stockIndex, serviceRangeIndex) {
      // If the last service range & stock item then start a new stock row
      if(stockIndex >= this.servicePricingData.stockServicePriceList.length-1 && serviceRangeIndex >= this.servicePricingData.servicePriceGroupList[this.selectedPriceGroup].servicePriceRanges.length-1) {
        evt.preventDefault()
        this.newStockButtonPressed()
      }
    }
  }  
}
</script>

